import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments } from "slices/interpreter/dashboardSlice";
import { interpreterLegends, LegendButton } from "components/common/TableColor";
import Swal from "sweetalert2";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";

function ExcelAppointmentList() {
  const dispatch = useDispatch();
  const { interpreterDashboard: dashboard, last_page: totalPages, loader } = useSelector(InterpreterDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const [activeLegends, setActiveLegends] = useState([0]);
  const [statusValues, setStatusValues] = useState([]);
  const [languageState, setLanguageState] = useState([]);
  const tableRef = useRef(null);

  const today = moment().format('YYYY-MM-DD');

  const defaultValues = {
    start_date: new Date(),
    end_date: new Date(),
    appointment_id: "",
    student_name: "",
    requester: "",
    school_id: null,
    type: null,
    language_ids: [],
  };

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, reset, getValues } = useForm({
    defaultValues,
  });

  const baseParams = { all: true, excel_only: true, pageLength: 100, sortBy: "date", sortDirection: "asc", type: [0, 1] };
  const todayParams = { ...baseParams, start_date: today, end_date: today };
  const [filters, setFilters] = useState(todayParams);

  useEffect(() => {
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
  }, [dispatch]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const filteredSchools = React.useMemo(() =>
    schools?.map(s => ({ value: s?.id, label: s?.name })), [schools]);

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends].filter(l => l.value !== 'day_off');
    temp_array.unshift({ value: "all", name: "All", id: 0, color: "#3176A0" });
    return temp_array;
  }, []);

  const buildParams = (formValues, statuses) => ({
    ...baseParams,
    start_date: formValues.start_date ? moment(formValues.start_date).format('YYYY-MM-DD') : undefined,
    end_date: formValues.end_date ? moment(formValues.end_date).format('YYYY-MM-DD') : undefined,
    appointment_id: formValues.appointment_id || undefined,
    student_name: formValues.student_name || undefined,
    requester: formValues.requester || undefined,
    school_id: formValues.school_id || undefined,
    type: (formValues.type !== null && formValues.type !== undefined) ? [formValues.type] : [0, 1],
    language_ids: formValues.language_ids?.length ? formValues.language_ids : undefined,
    status: statuses?.length ? statuses : undefined,
  });

  const handleActiveLegend = (id, value) => {
    const isAll = value === "all";
    const isAlreadyActive = activeLegends.includes(id);
    if (isAll || isAlreadyActive) {
      setActiveLegends([0]);
      setStatusValues([]);
      const params = buildParams(getValues(), []);
      setFilters(params);
      dispatch(fetchInterpreterDashboardAppointments(1, params));
    } else {
      setActiveLegends([id]);
      setStatusValues([value]);
      const params = buildParams(getValues(), [value]);
      setFilters(params);
      dispatch(fetchInterpreterDashboardAppointments(1, params));
    }
  };

  const onSubmit = (values) => {
    dispatch(setError({}));
    const rawId = values.appointment_id?.trim();
    const normalizedId = rawId ? rawId.replace(/^A/i, '') : '';
    const params = normalizedId
      ? { ...baseParams, appointment_id: normalizedId }
      : buildParams(values, statusValues);
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    clearErrors();
  };

  const handleResetFields = () => {
    reset(defaultValues);
    setLanguageState([]);
    setActiveLegends([0]);
    setStatusValues([]);
    setFilters(todayParams);
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
    clearErrors();
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Excel Appointments</h2>
        </div>

        <div className="card_table">
          <div className="button-sm forLegends p-3 gap-1">
            <LegendButton activeIds={activeLegends} options={filteredStatuses} handleClick={handleActiveLegend} />
          </div>
          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter appointments">
              <DateRangeFilter control={control} errors={errors} />

              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter ID" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Student Name</h2>
                <Controller
                  name="student_name"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Student Name" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Requester</h2>
                <Controller
                  name="requester"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Requester" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>School</h2>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{ ...selectCustomStyles }}
                        value={field.value ? filteredSchools?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredSchoolLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => field.onChange(obj?.value ?? null)}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Type</h2>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        styles={selectCustomStyles}
                        options={[
                          { value: 0, label: "Interpreting Request" },
                          { value: 1, label: "After Hours Request" },
                        ]}
                        isClearable
                        placeholder="Select Type"
                        onChange={(option) => field.onChange(option !== null ? option.value : null)}
                        value={
                          field.value !== null && field.value !== undefined
                            ? [
                                { value: 0, label: "Interpreting Request" },
                                { value: 1, label: "After Hours Request" },
                              ].find((opt) => opt.value === field.value)
                            : null
                        }
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Languages</h2>
                <Controller
                  name="language_ids"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }} className="multiselect">
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{ ...selectCustomStyles }}
                        value={languageState}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(selected) => {
                          setLanguageState(selected);
                          field.onChange((selected || []).map((opt) => opt.value));
                        }}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between btns_area">
              <div className="row-primary row_10">
                <SubmitButton buttonType="submit" btnText="Search" className="green" />
                <SubmitButton buttonType="button" className="lightBlue" btnText="Reset" handleSubmit={handleResetFields} />
              </div>
            </div>

            <div ref={tableRef}>
              <ListTable
                data={dashboard?.appointments}
                lunchRecords={[]}
                showLunch={false}
                filtration={filters}
                showInterpreter={true}
              />
            </div>
          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default ExcelAppointmentList;
